import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiJson } from '@/api/client'
import type { BulkLinkToCasePayload } from '@/features/alerts/types'
import { listUnlinkedAlerts } from '../api'

export const unlinkedAlertsKey = ['alerts', 'unlinked', 'recent'] as const

export function useUnlinkedAlerts(pageSize = 10) {
  return useQuery({
    queryKey: [...unlinkedAlertsKey, pageSize] as const,
    queryFn: () => listUnlinkedAlerts(pageSize),
    staleTime: 15_000,
  })
}

export interface LinkToCaseResult {
  case_id: string
  created: boolean
  updated: number
  skipped?: number
}

/**
 * Create a case from one alert (case_id null) or retry linking the alert
 * to an already-created case (case_id set). Uses the existing
 * POST /alerts/bulk/link-to-case endpoint. Deliberately no toast here: the
 * panel renders success/partial-failure inline so the outcome is never
 * silent. The caller must inspect `updated`: a 2xx with updated === 0
 * means the case exists but the alert was NOT linked.
 */
export function useCreateCaseFromAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkLinkToCasePayload) =>
      apiJson<LinkToCaseResult>('/alerts/bulk/link-to-case', {
        method: 'POST',
        body: payload,
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] })
      void qc.invalidateQueries({ queryKey: ['cases'] })
    },
  })
}
